import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, MapPin, Phone, Mail, Globe, Building2, Award, Shield, ArrowRight } from 'lucide-react';

const examinationCenters = [
  {
    id: 'mumbai',
    name: 'IMCAC Mumbai Examination Centre',
    city: 'Mumbai',
    country: 'India',
    status: 'Active',
    address: 'Unit 413, Swaroop Aditya Avenue, Marol Pipeline Rd, J B Nagar, Andheri East, Mumbai, Maharashtra 400099',
    phone: '+91 8382828215',
    email: 'mumbai@imcac.org',
    examinations: ['CBEAM', 'CBELM', 'CBERM', 'CBEAD', 'CBENA', 'CBEAA'],
    facilities: [
      'OSCE Simulation Labs',
      'Written Examination Hall',
      'Viva Examination Rooms',
      'Candidate Preparation Area',
      'Digital Assessment Systems',
    ],
  },
];

export default function ExaminationCentersPage() {
  return (
    <div data-testid="examination-centers-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division of Competency-Based Examination & Certification</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            Accredited Examination Centers
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            IMCAC-accredited examination centers meet rigorous standards for conducting competency-based assessments 
            in aesthetic, longevity, and regenerative medicine.
          </p>
        </div>
      </section>

      {/* Accreditation Standards */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <p className="imcac-overline mb-3">Accreditation Standards</p>
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">
                Centre Accreditation Requirements
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                All IMCAC examination centers undergo comprehensive accreditation review to ensure they meet 
                international standards for clinical competency assessment. Centers must demonstrate capability 
                in conducting written examinations, OSCE assessments, and viva voce evaluations.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                {[
                  'Purpose-built OSCE simulation facilities',
                  'Qualified examination invigilators',
                  'Secure digital assessment systems',
                  'Standardized patient programs',
                  'Emergency response protocols',
                  'Accessibility compliance',
                  'Data security infrastructure',
                  'Quality assurance processes',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-slate-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80"
                alt="Examination Centre"
                className="w-full h-64 object-cover rounded-sm shadow-md mb-6"
              />
              <Card className="imcac-card border-t-2 border-t-[#C5A572]">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Shield className="h-5 w-5 text-[#C5A572]" />
                    <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading">Quality Assurance</h3>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">
                    All examination centers undergo annual re-accreditation review and random quality audits 
                    to maintain IMCAC certification standards.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Examination Centers List */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="text-center mb-12">
            <p className="imcac-overline mb-3">Active Centers</p>
            <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading">IMCAC Examination Centers</h2>
          </div>

          <div className="space-y-6">
            {examinationCenters.map((center) => (
              <Card key={center.id} className="imcac-card overflow-hidden" data-testid={`center-${center.id}`}>
                <div className="bg-[#1B2A4A] px-6 py-4">
                  <div className="flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                      <Building2 className="h-6 w-6 text-[#C5A572]" />
                      <div>
                        <h3 className="text-xl font-bold text-white font-heading">{center.name}</h3>
                        <p className="text-sm text-slate-300">{center.city}, {center.country}</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500 text-white">{center.status}</Badge>
                  </div>
                </div>

                <CardContent className="p-6">
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Contact Information */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3">Contact Information</h4>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <MapPin className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-slate-600">{center.address}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="h-4 w-4 text-[#C5A572] flex-shrink-0" />
                          <span className="text-sm text-slate-600">{center.phone}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Mail className="h-4 w-4 text-[#C5A572] flex-shrink-0" />
                          <span className="text-sm text-slate-600">{center.email}</span>
                        </div>
                      </div>
                    </div>

                    {/* Examinations Offered */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3">Examinations Offered</h4>
                      <div className="flex flex-wrap gap-2">
                        {center.examinations.map((exam, i) => (
                          <Badge key={i} variant="outline" className="text-xs border-[#1B2A4A] text-[#1B2A4A]">
                            {exam}
                          </Badge>
                        ))}
                      </div>
                    </div>

                    {/* Facilities */}
                    <div>
                      <h4 className="text-sm font-semibold text-[#1B2A4A] mb-3">Facilities</h4>
                      <div className="space-y-1.5">
                        {center.facilities.map((facility, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <CheckCircle className="h-3 w-3 text-[#C5A572] flex-shrink-0" />
                            <span className="text-xs text-slate-600">{facility}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Become an Examination Center */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <Card className="imcac-card border-t-4 border-t-[#C5A572]">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
                <div className="lg:col-span-8">
                  <div className="flex items-center gap-2 mb-3">
                    <Award className="h-5 w-5 text-[#C5A572]" />
                    <p className="text-sm font-semibold text-[#C5A572] uppercase tracking-wider">Partner With IMCAC</p>
                  </div>
                  <h3 className="text-2xl font-semibold text-[#1B2A4A] font-heading mb-3">
                    Become an Accredited Examination Center
                  </h3>
                  <p className="text-base text-slate-600 leading-relaxed">
                    Medical institutions and training facilities interested in becoming IMCAC-accredited examination 
                    centers can apply through our Institutional Accreditation Division. Centers must demonstrate 
                    appropriate infrastructure, qualified personnel, and commitment to examination integrity.
                  </p>
                </div>
                <div className="lg:col-span-4 text-center lg:text-right">
                  <Link to="/apply/examination-center" data-testid="apply-center-btn">
                    <Button className="bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm px-6">
                      Apply as Examination Center <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-10 bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-600 leading-relaxed">
                <span className="text-[#C5A572] font-semibold">Disclaimer:</span> Only examinations conducted 
                at IMCAC-accredited centers are recognized for certification purposes. Candidates should verify 
                center accreditation status before registration. IMCAC reserves the right to modify, suspend, 
                or revoke center accreditation based on quality audit findings.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
